import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import PrivateBasicInformationRegister
  from '../../components/customer/BasicInformation/PrivateBasicInformationRegister';
import ServiceInformationRegister
  from '../../components/customer/ServiceInformation/ServiceInformationRegister';
import Button from '../../components/common/Button/Button';
import styles from './NewCustomerPage.module.css';
import Customer from '../../models/Customer';
import { createNewCustomer } from '../../services/customer/customerService';
import APIError from '../../services/APIError';
import { isValidCustomer } from '../../utils/validation';
import LegalBasicInformationRegister
  from '../../components/customer/BasicInformation/LegalBasicInformationRegister';
import CustomerSectionWrapper
  from '../../components/layout/CustomerSectionWrapper/CustomerSectionWrapper';
import CustomerContext from '../../context/customer-context';

const initCustomer: Customer = {
  personType: '',
  firstName: '',
  name: '',
  country: '',
  email: '',
  registrationCode: '',
  phone: '',
  preferredLanguage: '',
  servicesProvided: [],
  questionnaireTypesRequired: [],
};

type NewCustomerPageProps = {
  customerType: 'PRIVATE' | 'LEGAL'
}

function NewCustomerPage(props: NewCustomerPageProps) {
  const navigation = useNavigate();
  const { t } = useTranslation();
  const [customer, setCustomer] = useState<Customer>(initCustomer);
  const [error, setError] = useState('');
  const customerCtx = useContext(CustomerContext);

  useEffect(() => {
    setCustomer({ ...initCustomer, personType: props.customerType });
  }, [props.customerType]);

  const updateCustomer = (newCustomer: Customer) => {
    setCustomer(newCustomer);
  };

  const isValidPrivateCustomer = (): boolean => customer.firstName !== '' &&
    isValidCustomer(customer);

  const saveCustomer = async () => {
    try {
      const createdCustomer = await createNewCustomer({ ...customer });
      customerCtx.setCustomer(createdCustomer);
      customerCtx.setSuccessMessage(t('customer.dashboard.newSuccessMessage'));
      navigation('/customer');
    } catch (err) {
      if (err instanceof APIError) {
        console.log(`Failed to save customer: ${err.errorBody}`);
        setError(t(`customer.validation.${err.errorBody}`));
      } else {
        console.log(err);
        setError(t(`customer.validation.creationFailed`));
      }
      window.scrollTo(0, 0);
    }
  };

  const handleRegister = async () => {
    const showError = props.customerType === 'LEGAL'
      ? !isValidCustomer(customer)
      : !isValidPrivateCustomer();
    if (showError) {
      setError(t('customer.validation.warningMessage'));
    } else {
      saveCustomer();
    }
  };

  const handleCancel = () => {
    navigation('/');
  };

  return (
    <div className={styles.sectionContainer}>
      {props.customerType === 'PRIVATE' &&
        <CustomerSectionWrapper
          title={t('customer.basicInfo.title')}
        >
          <PrivateBasicInformationRegister
            customer={customer}
            onUpdate={updateCustomer}
            error={error}
          />
        </CustomerSectionWrapper>
      }
      {props.customerType === 'LEGAL' &&
        <CustomerSectionWrapper
          title={t('customer.basicInfo.title')}
        >
          <LegalBasicInformationRegister
            customer={customer}
            onUpdate={updateCustomer}
            error={error}
          />
        </CustomerSectionWrapper>
      }
      <CustomerSectionWrapper
        title={t('customer.serviceInfo.title')}
      >
        <ServiceInformationRegister
          customer={customer}
          onUpdate={updateCustomer}
        />
      </CustomerSectionWrapper>
      <div className={styles.buttons}>
        <Button handleClick={handleRegister} text={t('customer.button.register')} />
        <Button handleClick={handleCancel} text={t('customer.button.cancel')} outline />
      </div>
    </div>
  );
}

export default NewCustomerPage;
